import { Pause } from "lucide-react";
import type { CarouselPhase } from "../hooks/useStageCarousel";

interface Props {
  phases: CarouselPhase[];
  currentIdx: number;
  progress: number; // 0–1
  isPaused: boolean;
}

/**
 * Phase progress strip rendered below StageHeader. Each phase is a small
 * track; the active one is wider and gets a lime fill that grows from 0→100%
 * as the phase elapses. Past phases are dim Paper, future phases are very
 * dim Paper. A Pause icon overlays the active track when paused.
 */
export default function CarouselProgress({
  phases,
  currentIdx,
  progress,
  isPaused,
}: Props) {
  if (phases.length <= 1) return null;

  return (
    <div className="flex-shrink-0 flex items-center justify-center gap-1.5 px-10 py-2">
      {phases.map((phase, i) => {
        const isActive = i === currentIdx;
        const isPast = i < currentIdx;
        // Stage Calm: only one phase kind exists. The label is constant and
        // this component early-returns when phases.length <= 1, so the strip
        // effectively renders nothing now — kept as a no-op until it can be
        // removed cleanly with its call sites.
        void phase;
        const label = "Cluster";

        return (
          <div
            key={i}
            className={`relative h-1 rounded-full transition-all duration-300 ${
              isActive
                ? "w-10 bg-rc-paper/12"
                : isPast
                ? "w-4 bg-rc-paper/25"
                : "w-4 bg-rc-paper/10"
            }`}
            aria-label={label}
          >
            {isActive && (
              <>
                <div
                  className={`absolute inset-y-0 left-0 h-1 rounded-full transition-none ${
                    isPaused ? "bg-rc-lime/40" : "bg-rc-lime"
                  }`}
                  style={{ width: `${Math.min(progress * 100, 100)}%` }}
                />
                {isPaused && (
                  <Pause
                    className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 text-rc-lime/80"
                    aria-hidden
                  />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
